import { useMemo } from "react"
import { type Address, parseEther } from "viem"
import { coinParser, floorNumberToDecimals, type Coins } from "thales-utils"
import { getSportsAMMV2Transaction } from "@/Overtime/utils/sportsAmmV2"
import { getContractInstance } from "@/Overtime/utils/contract"
import { ContractType } from "@/Overtime/enums/contract"
import { getCollateralAddress } from "@/Overtime/utils/collaterals"
import { getTradeData } from "@/Overtime/utils/marketsV2"
import type { TicketMarket } from "@/Overtime/types/markets"
import { OVER_ADDED_PAYOUT_PERCENTAGE } from "@/Overtime/constants/markets"
import { OVERTIME_AFFILIATE_ADDRESS } from "@/config/env"
import { type SupportedNetwork } from "@/Overtime/types/network"
import { useChainSpecificWallet } from "wallet"
import { isFeatureSupported } from "wallet/config/gasSponsorship"
import useChainProviderHook from "@/hooks/useChainProvider"

interface UseBetPlacementProps {
  ticketMarkets: TicketMarket[]
  betAmount: string
  usedCollateralForBuy: Coins
  selectedCollateralIndex: number
  systemBetDenominator: number
  walletClient: any
  client: any
  chainId: number
}

export const useBetPlacement = ({
  ticketMarkets,
  betAmount,
  usedCollateralForBuy,
  selectedCollateralIndex,
  systemBetDenominator,
  walletClient,
  client,
  chainId,
}: UseBetPlacementProps) => {
  const { isOvertimeChain } = useChainProviderHook()
  // Get gasless transaction functionality
  const { executeGaslessTransaction, hasGasSponsorship } =
    useChainSpecificWallet()

  // Check if we can use gasless transactions for bets on this chain
  const canUseBetSponsorship = useMemo(() => {
    return (
      chainId &&
      isFeatureSupported(chainId, "sponsorBets") &&
      hasGasSponsorship &&
      executeGaslessTransaction
    )
  }, [chainId, hasGasSponsorship, executeGaslessTransaction])

  const collateralAddress = useMemo(() => {
    if (!chainId || !isOvertimeChain) {
      return undefined
    }
    return getCollateralAddress(
      chainId as SupportedNetwork,
      selectedCollateralIndex
    )
  }, [chainId, selectedCollateralIndex])

  const isOverCurrency = (currencyKey: Coins) => {
    return currencyKey === "OVER"
  }

  const getAddedPayoutOdds = (currencyKey: Coins, odds: number) =>
    isOverCurrency(currencyKey)
      ? odds /
        (1 + OVER_ADDED_PAYOUT_PERCENTAGE - OVER_ADDED_PAYOUT_PERCENTAGE * odds)
      : odds

  const totalQuote = useMemo(() => {
    let quote = 0
    quote = ticketMarkets.reduce(
      (partialQuote, market) =>
        partialQuote *
        (market.odd > 0
          ? getAddedPayoutOdds(usedCollateralForBuy, market.odd)
          : market.odd),
      1
    )
    return quote
  }, [ticketMarkets, usedCollateralForBuy])

  const placeBet = async () => {
    const tradeData = getTradeData(ticketMarkets)

    const parsedBuyInAmount = coinParser(
      betAmount.toString(),
      chainId,
      usedCollateralForBuy
    )
    const parsedTotalQuote = parseEther(
      floorNumberToDecimals(totalQuote, 18).toString()
    )
    const additionalSlippage = parseEther("0.02")

    const networkConfig = {
      client: walletClient.data,
      networkId: chainId,
    }

    const sportsAMMV2Contract = getContractInstance(
      ContractType.SPORTS_AMM_V2,
      networkConfig
    )

    const freeBetHolderContract = getContractInstance(
      ContractType.FREE_BET_HOLDER,
      networkConfig
    )

    const txHash: Address = await getSportsAMMV2Transaction(
      collateralAddress,
      true,
      false,
      chainId,
      sportsAMMV2Contract,
      freeBetHolderContract,
      tradeData,
      parsedBuyInAmount,
      parsedTotalQuote,
      OVERTIME_AFFILIATE_ADDRESS, // REFFERRAL ADDRESS
      additionalSlippage,
      false,
      false,
      walletClient.data,
      false,
      systemBetDenominator,
      executeGaslessTransaction // Pass the gasless transaction function
    )

    return txHash
  }

  return {
    placeBet,
    totalQuote,
    collateralAddress,
    // Expose gasless transaction info
    hasGasSponsorship,
    canUseBetSponsorship,
    chainSupportsBetSponsorship: chainId
      ? isFeatureSupported(chainId, "sponsorBets")
      : false,
  }
}
